(() => {
  'use strict';
  if (customElements.get('lm-dog-discovery')) return;
  const normalize = value => String(value || '').normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const aliases = { frenchie: 'french bulldog', yorkie: 'yorkshire terrier', dobie: 'doberman', aussie: 'australian shepherd', corgi: 'pembroke welsh corgi', lab: 'labrador retriever', gsd: 'german shepherd', weim: 'weimaraner' };
  let runtimePromise;
  function safeAsset(value, filename) {
    const url = new URL(value, location.href);
    if (url.origin !== location.origin && (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com')) throw new Error('Invalid asset');
    if (!filename.test(url.pathname)) throw new Error('Unexpected asset');
    return url.href;
  }
  function loadRuntime(src) {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (runtimePromise) return runtimePromise;
    runtimePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const fail = () => { clearTimeout(timer); script.remove(); runtimePromise = null; reject(new Error('Viewer unavailable')); };
      const timer = setTimeout(fail, 15000);
      script.type = 'module';
      script.src = safeAsset(src, /\/dog-studio-model-viewer-4\.3\.1\.min\.js$/);
      script.addEventListener('error', fail, { once: true });
      script.addEventListener('load', () => { clearTimeout(timer); if (customElements.get('model-viewer')) resolve(); else fail(); }, { once: true });
      document.head.append(script);
    });
    return runtimePromise;
  }
  class DogDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.cards = [...this.querySelectorAll('[data-dog-card]')];
      this.input = this.querySelector('[data-breed-search]');
      this.dialog = this.querySelector('[data-preview-dialog]');
      this.strings = JSON.parse(this.querySelector('[data-lm-strings]').textContent);
      this.limit = 12;
      this.rail = this.querySelector('[data-dog-grid]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motionOn = !this.reducedMotion.matches && !navigator.connection?.saveData;
      this.updateMotion();
      this.querySelector('[data-rail-controls]').hidden = false;
      this.rail.addEventListener('scroll', () => this.updateArrows(), { signal, passive: true });
      this.rail.addEventListener('keydown', event => {
        if (event.target !== this.rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.moveRail(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.reducedMotion.addEventListener('change', () => { this.motionOn = !this.reducedMotion.matches; this.updateMotion(); }, { signal });
      this.filter = 'all';
      this.querySelector('[data-search-tools]').hidden = false;
      this.querySelectorAll('[data-preview]').forEach(button => { button.hidden = false; });
      this.restore();
      this.input.addEventListener('input', () => { this.limit = 12; this.update(true); }, { signal });
      this.addEventListener('click', event => {
        const target = event.target.closest('button');
        if (!target) return;
        if (target.matches('[data-dog-filter]')) { this.filter = target.dataset.dogFilter; this.limit = 12; this.update(true); }
        if (target.matches('[data-clear-search], [data-reset]')) { this.input.value = ''; this.filter = 'all'; this.limit = 12; this.update(true); this.input.focus(); }
        if (target.matches('[data-show-more]')) { this.limit += 12; this.update(); }
        if (target.matches('[data-preview]')) this.openPreview(target.closest('[data-dog-card]'), target);
        if (target.matches('[data-preview-close]')) this.dialog.close();
        if (target.matches('[data-start-model]')) this.startModel();
        if (target.matches('[data-rail-next]')) this.moveRail(1);
        if (target.matches('[data-rail-prev]')) this.moveRail(-1);
        if (target.matches('[data-rail-motion]')) { this.motionOn = !this.motionOn; this.updateMotion(); }
      }, { signal });
      this.dialog.addEventListener('close', () => { this.clearModel(); this.previewTrigger?.focus({ preventScroll: true }); }, { signal });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) { const b = this.dialog.getBoundingClientRect(); if (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom) this.dialog.close(); } }, { signal });
      this.querySelector('[data-preview-image]').addEventListener('error', () => { this.querySelector('[data-preview-image]').hidden = true; this.querySelector('[data-preview-missing]').hidden = false; }, { signal });
      this.cards.forEach(card => {
        const img = card.querySelector('img');
        const fail = () => { img.hidden = true; card.querySelector('[data-image-missing]').hidden = false; };
        if (img) { img.addEventListener('error', fail, { signal }); if (img.complete && img.naturalWidth === 0) fail(); }
      });
      window.addEventListener('popstate', () => { this.restore(); this.update(); }, { signal });
      window.addEventListener('pagehide', () => { if (this.dialog.open) this.dialog.close(); this.clearModel(); }, { signal });
      this.update();
    }
    disconnectedCallback() { this.events?.abort(); this.clearModel(); this.ready = false; }
    restore() {
      const params = new URL(location.href).searchParams;
      this.input.value = params.get('dog_q') || '';
      this.filter = ['new', 'model'].includes(params.get('dog_view')) ? params.get('dog_view') : 'all';
    }
    update(writeURL = false) {
      const raw = normalize(this.input.value);
      const query = aliases[raw] || raw;
      const matches = this.cards.filter(card => normalize(card.dataset.breed).includes(query) && (this.filter !== 'new' || card.dataset.new === 'true') && (this.filter !== 'model' || !!card.dataset.model));
      const visible = new Set(matches.slice(0, this.limit));
      this.cards.forEach(card => { card.hidden = !visible.has(card); });
      this.querySelectorAll('[data-dog-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.dogFilter === this.filter)));
      this.querySelector('[data-result-count]').textContent = this.strings.results.replace('[shown]', String(visible.size)).replace('[count]', String(matches.length));
      this.querySelector('[data-no-results]').hidden = matches.length !== 0;
      this.querySelector('[data-show-more]').hidden = matches.length <= this.limit;
      if (writeURL) this.rail.scrollLeft = 0;
      requestAnimationFrame(() => this.updateArrows());
      if (writeURL) {
        const url = new URL(location.href);
        this.input.value ? url.searchParams.set('dog_q', this.input.value) : url.searchParams.delete('dog_q');
        this.filter !== 'all' ? url.searchParams.set('dog_view', this.filter) : url.searchParams.delete('dog_view');
        try { history.replaceState(history.state, '', url); } catch (_) {}
      }
    }
    moveRail(direction) {
      this.rail.scrollBy({ left: direction * this.rail.clientWidth * 0.8, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }
    updateArrows() {
      this.querySelector('[data-rail-prev]').disabled = this.rail.scrollLeft <= 2;
      this.querySelector('[data-rail-next]').disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 2;
    }
    updateMotion() {
      this.classList.toggle('has-gentle-motion', this.motionOn && !this.reducedMotion.matches);
      const button = this.querySelector('[data-rail-motion]');
      button.hidden = this.reducedMotion.matches;
      button.setAttribute('aria-pressed', String(this.motionOn));
      button.textContent = this.motionOn ? this.strings.pause : this.strings.play;
    }
    clearModel() {
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.modelTimeout);
      const stage = this.querySelector('[data-model-stage]');
      stage?.replaceChildren();
      this.querySelector('.lm-preview-art')?.classList.remove('is-model-ready');
      this.loadingModel = false;
    }
    openPreview(card, trigger) {
      if (this.dialog.open) return;
      this.clearModel();
      this.previewTrigger = trigger;
      this.modelURL = card.dataset.model;
      this.breed = card.dataset.breed;
      this.querySelector('[data-preview-title]').textContent = this.breed;
      const source = card.querySelector('img');
      const image = this.querySelector('[data-preview-image]');
      image.hidden = !source || source.hidden;
      this.querySelector('[data-preview-missing]').hidden = !image.hidden;
      if (source) { image.src = source.currentSrc || source.src; image.alt = source.alt; } else image.removeAttribute('src');
      this.querySelector('[data-preview-buy]').href = card.querySelector('[data-product-link]').href;
      this.querySelector('[data-preview-state]').textContent = this.modelURL ? this.strings.ready : this.strings.static;
      const button = this.querySelector('[data-start-model]');
      button.hidden = !this.modelURL;
      button.disabled = false;
      this.dialog.showModal();
    }
    async startModel() {
      if (this.loadingModel || !this.modelURL || !this.dialog.open) return;
      this.loadingModel = true;
      const generation = this.generation;
      const button = this.querySelector('[data-start-model]');
      const status = this.querySelector('[data-preview-state]');
      button.disabled = true;
      status.textContent = this.strings.loading;
      const fail = () => {
        if (generation !== this.generation) return;
        this.clearModel();
        status.textContent = this.strings.error;
        button.disabled = false;
        button.hidden = false;
      };
      try {
        const src = safeAsset(this.modelURL, /\/dog-studio-[a-z0-9-]+-[a-f0-9]{12}\.glb$/);
        await loadRuntime(this.dataset.runtime);
        if (generation !== this.generation || !this.dialog.open) return;
        const model = document.createElement('model-viewer');
        model.setAttribute('alt', this.strings.modelLabel.replace('[breed]', this.breed));
        model.setAttribute('camera-controls', '');
        model.setAttribute('touch-action', 'pan-y');
        model.setAttribute('shadow-intensity', '0.6');
        model.setAttribute('interaction-prompt', 'none');
        model.setAttribute('environment-image', 'neutral');
        model.setAttribute('loading', 'eager');
        model.setAttribute('reveal', 'auto');
        model.setAttribute('tabindex', '0');
        model.addEventListener('load', () => {
          if (generation !== this.generation || !this.dialog.open) return;
          clearTimeout(this.modelTimeout);
          this.querySelector('.lm-preview-art').classList.add('is-model-ready');
          status.textContent = this.strings.loaded;
          button.hidden = true;
          if (!this.dialog.contains(document.activeElement) || document.activeElement === button || document.activeElement === this.dialog) model.focus({ preventScroll: true });
        }, { once: true });
        model.addEventListener('error', fail, { once: true });
        this.modelTimeout = setTimeout(fail, 20000);
        model.src = src;
        this.querySelector('[data-model-stage]').replaceChildren(model);
      } catch (_) { fail(); }
    }
  }
  customElements.define('lm-dog-discovery', DogDiscovery);
})();
